import React, { useMemo } from 'react';
import { Line } from '../types';
import { LINE_COLUMNS } from '../constants/columns';
import EditableCell from './EditableCell';
import SerialNumberFilter from './SerialNumberFilter';
import { useSubinventoryLov } from '../hooks/useSubinventoryLov';
import { useLocatorLov } from '../hooks/useLocatorLov';

interface LinesTableProps {
  lines: Line[];
  isDarkTheme?: boolean;
  onLineUpdate?: (lineId: string, field: string, value: string) => void;
  selectedHeaderToOrg?: string;
  newRowIds?: Set<string>;
}

const LinesTable: React.FC<LinesTableProps> = ({ lines, isDarkTheme, onLineUpdate, selectedHeaderToOrg, newRowIds = new Set() }) => {
  const [serialFilter, setSerialFilter] = React.useState('');
  const { data: subinventories, refetch: refetchSubinventories } = useSubinventoryLov(selectedHeaderToOrg, false);
  const { refetch: refetchLocators } = useLocatorLov(selectedHeaderToOrg, '', false);

  const isQueryMode = useMemo(() => {
    return lines.some(line => line.validatedSerialNumber && line.validatedSerialNumber.trim() !== '');
  }, [lines]);

  const filteredLines = useMemo(() => {
    if (!serialFilter) return lines;
    
    const selectedLine = lines.find(line => line.serialNumber === serialFilter);
    const otherLines = lines.filter(line => line.serialNumber !== serialFilter);
    
    return selectedLine ? [selectedLine, ...otherLines] : lines;
  }, [lines, serialFilter]);
  const getLineValue = (line: Line, column: string): string => {
    const fieldMap: Record<string, keyof Line> = {
      'Description': 'description',
      'Serial Number': 'serialNumber',
      'Asset Number': 'assetNumber',
      'Validated Serial Number': 'validatedSerialNumber',
      'Validated Asset Number': 'validatedAssetNumber',
      'Item Number': 'itemNumber',
      'To Subinventory': 'toSubinventory',
      'To Locator': 'toLocator',
      'Is Serial Valid': 'isSerialValid',
      'Validated Serial Item': 'validatedSerialItem',
      'Validated Serial Site': 'validatedSerialSite',
      'Status': 'status'
    };
    const value = line[fieldMap[column]];
    return typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value || '');
  };

  const getStatusBackgroundColor = (status: string): string => {
    const statusLower = status.toLowerCase();
    if (statusLower === 'success') return '#d4edda';
    if (statusLower === 'error') return '#f8d7da';
    return 'transparent';
  };

  const getStatusTextColor = (status: string): string => {
    const statusLower = status.toLowerCase();
    if (statusLower === 'success') return '#155724';
    if (statusLower === 'error') return '#721c24';
    return isDarkTheme ? '#fff' : '#000';
  };

  return (
    <div style={{ maxHeight: '400px', overflowY: 'auto', border: `1px solid ${isDarkTheme ? '#555' : '#ccc'}` }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ backgroundColor: isDarkTheme ? '#444' : '#f5f5f5' }}>
            {LINE_COLUMNS.map((column) => (
              <th key={column} style={{ padding: '4px', border: `1px solid ${isDarkTheme ? '#555' : '#ccc'}`, textAlign: 'left', color: isDarkTheme ? '#fff' : '#000', position: 'sticky', top: 0, backgroundColor: isDarkTheme ? '#444' : '#e0e0e0', fontSize: '13px', zIndex: 10 }}>
                {column}
                {column === 'Serial Number' && (
                  <SerialNumberFilter
                    lines={lines}
                    isDarkTheme={isDarkTheme}
                    onFilterChange={setSerialFilter}
                  />
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filteredLines.length === 0 ? (
            <tr style={{ backgroundColor: isDarkTheme ? '#2a2a2a' : 'white', color: isDarkTheme ? '#fff' : '#000' }}>
              {LINE_COLUMNS.map((column) => (
                <td key={column} style={{ padding: '4px', border: `1px solid ${isDarkTheme ? '#555' : '#ccc'}`, color: isDarkTheme ? '#fff' : '#000', fontSize: '12px' }}>
                  
                </td>
              ))}
            </tr>
          ) : (
            filteredLines.map((line) => (
              <tr key={line.id} style={{ 
                backgroundColor: line.validatedSerialNumber 
                  ? (isDarkTheme ? '#1b5e20' : '#e8f5e8') 
                  : (isDarkTheme ? '#2a2a2a' : 'white'), 
                color: isDarkTheme ? '#fff' : '#000' 
              }}>
                {LINE_COLUMNS.map((column) => {
                  const cellValue = getLineValue(line, column);
                  const isEditable = !isQueryMode && ['Validated Serial Number', 'Validated Asset Number', 'To Subinventory', 'To Locator'].includes(column);
                  
                  return (
                    <td key={column} style={{ 
                      padding: '4px', 
                      border: `1px solid ${isDarkTheme ? '#555' : '#ccc'}`, 
                      backgroundColor: column === 'Status' && cellValue ? getStatusBackgroundColor(cellValue) : 'inherit',
                      color: column === 'Status' ? getStatusTextColor(cellValue) : (isDarkTheme ? '#fff' : '#000'), 
                      fontSize: '12px'
                    }}>
                      {isEditable ? (
                        <EditableCell
                          column={column}
                          value={cellValue}
                          lineId={line.id}
                          line={line}
                          onUpdate={onLineUpdate!}
                          isDarkTheme={isDarkTheme}
                          selectedHeaderToOrg={selectedHeaderToOrg}
                          subinventories={subinventories}
                          refetchSubinventories={refetchSubinventories}
                          refetchLocators={refetchLocators}
                        />
                      ) : (
                        <div style={{ minHeight: '16px' }}>
                          {cellValue}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default LinesTable;